import React, { useState, useEffect, useCallback, useRef } from 'react';
import Confetti from 'react-dom-confetti';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { ColorPicker, Slider } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import {
  TbBold,
  TbItalic,
  TbStrikethrough,
  TbH1,
  TbH2,
  TbH3,
  TbList,
  TbListNumbers,
  TbAlignLeft,
  TbAlignCenter,
  TbAlignRight,
  TbTrash,
  TbCheckbox,
  TbPencil,
  TbSeparator,
  TbPalette,
  TbShare,
  TbPin,
  TbClockDown,
  TbX,
  TbRepeat,
} from 'react-icons/tb';
import './TaskDescriptionModal.css';
import { ShareTaskModal } from './ShareTaskModal';
import { SharedUser, SharedBy } from '../App';
import SwitchSelector from 'react-switch-selector';

interface TaskDescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskTitle: string;
  initialContent?: string;
  onSave: (title: string, content: string) => void;
  onTitleChange?: (newTitle: string) => void;
  createdAt?: number;
  taskId?: string;
  isCompleted?: boolean;
  onToggle?: (id: string) => void;
  timerStartedAt?: number;
  totalElapsedTime?: number;
  onTimerToggle?: (id: string) => void;
  willDoToday?: 'today' | 'pinned' | false;
  onWillDoTodayChange?: (
    id: string,
    willDoToday: 'today' | 'pinned' | false
  ) => void;
  sharedWith?: SharedUser[];
  sharedBy?: SharedBy;
  currentUserId?: string;
  onShareSuccess?: (email: string, userId: string, photoURL?: string) => void;
  onRemoveSharedUser?: (userId: string, email: string) => void;
  onDelete?: (id: string) => void;
  urgency?: 'high' | 'medium' | 'low' | null;
  urgencyNumber?: number;
  onUrgencyChange?: (id: string, urgencyNumber: number) => void;
  onSnooze?: (id: string, snoozedUntil: number) => void;
  userTimeTracking?: { [userId: string]: number };
  recurrenceDays?: number;
  completionLog?: number[];
  onRecurrenceChange?: (id: string, recurrenceDays: number | null) => void;
  onCompletionLogChange?: (id: string, completionLog: number[]) => void;
}

export const TaskDescriptionModal: React.FC<TaskDescriptionModalProps> = ({
  isOpen,
  onClose,
  taskTitle,
  initialContent = '',
  onSave,
  onTitleChange,
  createdAt,
  taskId,
  isCompleted = false,
  onToggle,
  timerStartedAt,
  totalElapsedTime,
  onTimerToggle,
  willDoToday = false,
  onWillDoTodayChange,
  sharedWith = [],
  sharedBy,
  currentUserId,
  onShareSuccess,
  onRemoveSharedUser,
  onDelete,
  urgency,
  urgencyNumber = 1,
  onUrgencyChange,
  onSnooze,
  userTimeTracking = {},
  recurrenceDays,
  completionLog = [],
  onRecurrenceChange,
  onCompletionLogChange,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(taskTitle);
  const [editedContent, setEditedContent] = useState(initialContent);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [gotLogged, setGotLogged] = useState(false);
  const [appearsChecked, setAppearsChecked] = useState(false);
  const [currentElapsedTime, setCurrentElapsedTime] = useState(0);
  const timerIntervalRef = useRef<number | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [switcherTooltip, setSwitcherTooltip] = useState<string | null>(null);
  const [switcherTooltipPosition, setSwitcherTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [localWillDoToday, setLocalWillDoToday] = useState<
    'today' | 'pinned' | false
  >(willDoToday);
  const [isSnoozeDropdownOpen, setIsSnoozeDropdownOpen] = useState(false);
  const [isUrgencyDropdownOpen, setIsUrgencyDropdownOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [pendingUrgencyNumber, setPendingUrgencyNumber] =
    useState(urgencyNumber);
  const [hasUrgencyChanges, setHasUrgencyChanges] = useState(false);
  const [deleteTooltipPosition, setDeleteTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const snoozeDropdownRef = useRef<HTMLDivElement>(null);
  const urgencyDropdownRef = useRef<HTMLDivElement>(null);
  const recurrenceDropdownRef = useRef<HTMLDivElement>(null);
  const [isRecurrenceDropdownOpen, setIsRecurrenceDropdownOpen] = useState(false);
  const [customRecurrenceDays, setCustomRecurrenceDays] = useState('');
  const [isCompletionLogExpanded, setIsCompletionLogExpanded] = useState(false);

  // Debounced save function
  const debouncedSave = useCallback(
    (title: string, content: string) => {
      const timeoutId = setTimeout(() => {
        if (title !== taskTitle && onTitleChange) {
          onTitleChange(title);
        }
        onSave(title, content);
      }, 2000);
      return () => clearTimeout(timeoutId);
    },
    [onSave, onTitleChange, taskTitle]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      HorizontalRule,
      TextStyle,
      Color,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();
      setEditedContent(newContent);
      debouncedSave(editedTitle, newContent);
    },
  });

  // Reset content when modal opens with new initialContent
  useEffect(() => {
    if (isOpen && editor) {
      editor.commands.setContent(initialContent || '');
      setEditedContent(initialContent || '');
      setEditedTitle(taskTitle);
      setPendingUrgencyNumber(urgencyNumber);
      setHasUrgencyChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialContent, editor, taskTitle]);

  // Update pending urgency when urgency changes (e.g., after accepting changes)
  useEffect(() => {
    setPendingUrgencyNumber(urgencyNumber);
  }, [urgencyNumber]);

  // Sync local willDoToday state with prop
  useEffect(() => {
    setLocalWillDoToday(willDoToday);
  }, [willDoToday]);

  // Reset appearsChecked when completion log changes (e.g., entry deleted)
  useEffect(() => {
    if (!isCompleted) {
      setAppearsChecked(false);
    }
  }, [completionLog?.length, isCompleted]);

  // Timer effect
  useEffect(() => {
    if (timerStartedAt) {
      // Calculate initial elapsed time
      const now = Date.now();
      const sessionElapsed = Math.floor((now - timerStartedAt) / 1000);
      const total = (totalElapsedTime || 0) + sessionElapsed;
      setCurrentElapsedTime(total);

      // Update timer every second
      timerIntervalRef.current = window.setInterval(() => {
        const now = Date.now();
        const sessionElapsed = Math.floor((now - timerStartedAt) / 1000);
        const total = (totalElapsedTime || 0) + sessionElapsed;
        setCurrentElapsedTime(total);
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    } else {
      // Clear any existing interval
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setCurrentElapsedTime(totalElapsedTime || 0);
    }
  }, [timerStartedAt, totalElapsedTime]);

  // Add click outside handler for color picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-picker-container')) {
        setIsColorPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected color when text style changes
  useEffect(() => {
    if (editor) {
      const currentColor = editor.getAttributes('textStyle').color;
      if (currentColor) {
        setSelectedColor(currentColor);
      }
    }
  }, [editor]);

  const handleEditorClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A') {
      e.preventDefault();
      const href = target.getAttribute('href');
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(false);
    debouncedSave(editedTitle, editedContent);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTitleEdit();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      setEditedTitle(taskTitle);
    }
  };

  // Auto-resize title textarea to fit content
  const adjustTitleTextareaHeight = () => {
    if (titleTextareaRef.current) {
      const textarea = titleTextareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (isEditingTitle) {
      // Small delay to ensure the textarea is rendered
      setTimeout(adjustTitleTextareaHeight, 0);
    }
  }, [isEditingTitle]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedTitle(e.target.value);
    adjustTitleTextareaHeight();
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        snoozeDropdownRef.current &&
        !snoozeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSnoozeDropdownOpen(false);
        setShowDatePicker(false);
      }
      if (
        urgencyDropdownRef.current &&
        !urgencyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUrgencyDropdownOpen(false);
      }
      if (
        recurrenceDropdownRef.current &&
        !recurrenceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRecurrenceDropdownOpen(false);
        setCustomRecurrenceDays('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Snooze handlers
  const handleTomorrowSnooze = () => {
    if (taskId && onSnooze) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      onSnooze(taskId, tomorrow.getTime());
      setIsSnoozeDropdownOpen(false);
      onClose();
    }
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleDateSubmit = () => {
    if (taskId && selectedDate && onSnooze) {
      onSnooze(taskId, selectedDate.getTime());
      setIsSnoozeDropdownOpen(false);
      setShowDatePicker(false);
      setSelectedDate(null);
      onClose();
    }
  };

  // Urgency handlers
  const handlePendingUrgencyChange = (value: number) => {
    setPendingUrgencyNumber(value);
    setHasUrgencyChanges(value !== urgencyNumber);
  };

  const handleAcceptUrgency = () => {
    if (taskId && onUrgencyChange && hasUrgencyChanges) {
      onUrgencyChange(taskId, pendingUrgencyNumber);
      setHasUrgencyChanges(false);
      setIsUrgencyDropdownOpen(false);
    }
  };

  const handleCancelUrgency = () => {
    setPendingUrgencyNumber(urgencyNumber);
    setHasUrgencyChanges(false);
    setIsUrgencyDropdownOpen(false);
  };

  // Recurrence handlers
  const handleSetRecurrence = (days: number) => {
    if (taskId && onRecurrenceChange) {
      onRecurrenceChange(taskId, days);
      setIsRecurrenceDropdownOpen(false);
      setCustomRecurrenceDays('');
    }
  };

  const handleRemoveRecurrence = () => {
    if (taskId && onRecurrenceChange) {
      onRecurrenceChange(taskId, null);
      setIsRecurrenceDropdownOpen(false);
    }
  };

  const handleCustomRecurrenceSubmit = () => {
    const days = parseInt(customRecurrenceDays, 10);
    if (days > 0 && taskId && onRecurrenceChange) {
      onRecurrenceChange(taskId, days);
      setIsRecurrenceDropdownOpen(false);
      setCustomRecurrenceDays('');
    }
  };

  const handleDeleteCompletionEntry = (timestamp: number) => {
    if (taskId && onCompletionLogChange && completionLog) {
      const newLog = completionLog.filter(t => t !== timestamp);
      onCompletionLogChange(taskId, newLog);
    }
  };

  if (!isOpen) return null;

  const handleClearBody = () => {
    if (editor) {
      editor.commands.setContent('');
      onSave(editedTitle, '');
    }
  };

  const handleCheckboxToggle = () => {
    if (!isCompleted && taskId && onToggle) {
      // Set visual state immediately
      setAppearsChecked(true);
      setGotLogged(true);

      // Delay the actual state change
      setTimeout(() => {
        onToggle(taskId);
        setGotLogged(false);
      }, 1000);
    } else if (taskId && onToggle) {
      onToggle(taskId);
      setAppearsChecked(false);
    }
  };

  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimerToggle = () => {
    if (taskId && onTimerToggle) {
      onTimerToggle(taskId);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content task-description-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <Confetti
            active={gotLogged}
            config={{
              angle: 90,
              spread: 360,
              startVelocity: 40,
              elementCount: 70,
              dragFriction: 0.12,
              duration: 3000,
              stagger: 3,
              width: '10px',
              height: '10px',
              colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
            }}
          />
          {taskId && onToggle && (
            <input
              type="checkbox"
              className="modal-task-checkbox"
              checked={appearsChecked || isCompleted}
              onChange={handleCheckboxToggle}
            />
          )}
          {isEditingTitle ? (
            <textarea
              ref={titleTextareaRef}
              className="modal-title-edit-input"
              value={editedTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleEdit}
              onKeyDown={handleTitleKeyDown}
              onClick={e => e.stopPropagation()}
              autoFocus
              rows={1}
            />
          ) : (
            <h2 className="modal-title" onClick={() => setIsEditingTitle(true)}>
              <TbPencil className="title-edit-icon" />
              {editedTitle}
            </h2>
          )}
          {taskId && onDelete && (
            <>
              <button
                className="modal-delete-btn"
                onClick={e => {
                  e.stopPropagation();
                  onDelete(taskId);
                  onClose();
                }}
                onMouseEnter={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDeleteTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => setDeleteTooltipPosition(null)}
              >
                <TbTrash />
              </button>
              {deleteTooltipPosition && (
                <div
                  className="delete-tooltip-floating"
                  style={{
                    left: deleteTooltipPosition.x,
                    top: deleteTooltipPosition.y,
                  }}
                >
                  Delete task
                </div>
              )}
            </>
          )}
        </div>
        <p className="modal-description-label">Description</p>
        <div className="editor-container">
          <div className="editor-toolbar">
            <button
              onClick={() => editor?.chain().focus().toggleTaskList().run()}
              className={editor?.isActive('taskList') ? 'is-active' : ''}
              title="Task List"
            >
              <TbCheckbox />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              className={editor?.isActive('bulletList') ? 'is-active' : ''}
              title="Bullet List"
            >
              <TbList />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              className={editor?.isActive('orderedList') ? 'is-active' : ''}
              title="Numbered List"
            >
              <TbListNumbers />
            </button>
            <div className="toolbar-separator" />
            <button
              onClick={() => editor?.chain().focus().setHorizontalRule().run()}
              title="Horizontal Rule"
            >
              <TbSeparator />
            </button>
            <div className="toolbar-separator" />
            <button
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={editor?.isActive('bold') ? 'is-active' : ''}
              title="Bold"
            >
              <TbBold />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={editor?.isActive('italic') ? 'is-active' : ''}
              title="Italic"
            >
              <TbItalic />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              className={editor?.isActive('strike') ? 'is-active' : ''}
              title="Strikethrough"
            >
              <TbStrikethrough />
            </button>
            <div className="toolbar-separator" />
            <button
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 1 }).run()
              }
              className={
                editor?.isActive('heading', { level: 1 }) ? 'is-active' : ''
              }
              title="Heading 1"
            >
              <TbH1 />
            </button>
            <button
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 2 }).run()
              }
              className={
                editor?.isActive('heading', { level: 2 }) ? 'is-active' : ''
              }
              title="Heading 2"
            >
              <TbH2 />
            </button>
            <button
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 3 }).run()
              }
              className={
                editor?.isActive('heading', { level: 3 }) ? 'is-active' : ''
              }
              title="Heading 3"
            >
              <TbH3 />
            </button>
            <div className="toolbar-separator" />
            <button
              onClick={() => editor?.chain().focus().setTextAlign('left').run()}
              className={
                editor?.isActive({ textAlign: 'left' }) ? 'is-active' : ''
              }
              title="Align Left"
            >
              <TbAlignLeft />
            </button>
            <button
              onClick={() =>
                editor?.chain().focus().setTextAlign('center').run()
              }
              className={
                editor?.isActive({ textAlign: 'center' }) ? 'is-active' : ''
              }
              title="Align Center"
            >
              <TbAlignCenter />
            </button>
            <button
              onClick={() =>
                editor?.chain().focus().setTextAlign('right').run()
              }
              className={
                editor?.isActive({ textAlign: 'right' }) ? 'is-active' : ''
              }
              title="Align Right"
            >
              <TbAlignRight />
            </button>
            <div className="toolbar-separator" />
            <div className="color-picker-container">
              <button
                onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                className={editor?.isActive('textStyle') ? 'is-active' : ''}
                title="Text Color"
                style={{ color: selectedColor }}
              >
                <TbPalette />
              </button>
              {isColorPickerOpen && (
                <div className="color-picker-dropdown">
                  <ColorPicker
                    format="hex"
                    value={selectedColor}
                    onChange={color => {
                      setSelectedColor(color);
                      editor?.chain().focus().setColor(color).run();
                    }}
                    swatches={[
                      '#000000',
                      '#25262b',
                      '#868e96',
                      '#fa5252',
                      '#e64980',
                      '#be4bdb',
                      '#7950f2',
                      '#4c6ef5',
                      '#228be6',
                      '#15aabf',
                      '#12b886',
                      '#40c057',
                      '#82c91e',
                      '#fab005',
                      '#fd7e14',
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
          <div onClick={handleEditorClick}>
            <EditorContent editor={editor} />
          </div>
        </div>
        {completionLog && completionLog.length > 0 && (
          <div className="completion-log-section">
            <button
              className="completion-log-toggle"
              onClick={() => setIsCompletionLogExpanded(!isCompletionLogExpanded)}
            >
              <span className="completion-log-summary">
                <TbRepeat size={14} />
                Completed {completionLog.length} time{completionLog.length > 1 ? 's' : ''}
                {' · '}
                Last: {new Date(completionLog[completionLog.length - 1]).toLocaleDateString()}
              </span>
              <span className={`completion-log-arrow ${isCompletionLogExpanded ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>
            {isCompletionLogExpanded && (
              <div className="completion-log-list">
                {[...completionLog].reverse().map((timestamp, index) => (
                  <div key={timestamp} className="completion-log-item">
                    <span className="completion-log-number">#{completionLog.length - index}</span>
                    <span className="completion-log-date">
                      {new Date(timestamp).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                    {onCompletionLogChange && (
                      <button
                        className="completion-log-delete"
                        onClick={() => handleDeleteCompletionEntry(timestamp)}
                        title="Delete this entry"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="task-meta-row">
          {createdAt && (
            <div className="task-created-date">
              Created on{' '}
              {new Date(createdAt).toLocaleDateString('en-US', {
                weekday: 'short',
              }) +
                ' ' +
                new Date(createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
            </div>
          )}
          {taskId && onTimerToggle && (
            <>
              {timerStartedAt && (
                <div className="modal-timer-display">
                  <span className="timer-label">Timer: </span>
                  <span className="timer-time">
                    {formatElapsedTime(currentElapsedTime)}
                  </span>
                </div>
              )}
              {!timerStartedAt && currentElapsedTime > 0 && (
                <div className="modal-timer-display paused">
                  <span className="timer-label">Time spent: </span>
                  <span className="timer-time">
                    {formatElapsedTime(currentElapsedTime)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        {sharedBy && (
          <div className="task-owner-info">
            <div className="owner-email-row">
              Owner: <strong>{sharedBy.ownerEmail}</strong>
            </div>
            <div className="owner-time-row">
              {((): string => {
                const ownerTime =
                  (userTimeTracking &&
                    userTimeTracking[sharedBy.ownerUserId]) ||
                  0;
                const minutes = Math.floor(ownerTime / 60);
                const seconds = ownerTime % 60;
                return `Time spent: ${minutes}m ${seconds}s`;
              })()}
            </div>
          </div>
        )}
        {!sharedBy && sharedWith.length > 0 && (
          <div className="shared-with-section">
            <div className="shared-with-header">Shared with:</div>
            <div className="shared-with-list">
              {sharedWith.map(user => {
                const userTime = userTimeTracking[user.userId] || 0;
                const minutes = Math.floor(userTime / 60);
                const seconds = userTime % 60;
                const timeDisplay = `${minutes}m ${seconds}s`;

                return (
                  <div key={user.userId} className="shared-with-item">
                    <div className="shared-with-user">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.email}
                          className="shared-with-avatar"
                        />
                      ) : (
                        <div className="shared-with-initials">
                          {user.email.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="shared-with-info">
                        <span className="shared-with-email">{user.email}</span>
                        <span className="shared-with-time">
                          Time spent: {timeDisplay}
                        </span>
                      </div>
                    </div>
                    {onRemoveSharedUser && (
                      <button
                        className="remove-shared-user-btn"
                        onClick={() =>
                          onRemoveSharedUser(user.userId, user.email)
                        }
                        title="Remove user"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="modal-actions">
          <div className="modal-actions-row-1">
            <button
              className="clear-btn"
              onClick={handleClearBody}
              title="Clear description"
            >
              <TbTrash />
              Clear body
            </button>
            {taskId && !sharedBy && (
              <button
                className="share-btn"
                onClick={e => {
                  e.stopPropagation();
                  setIsShareModalOpen(true);
                }}
                title="Share task"
              >
                <TbShare />
                Share
              </button>
            )}
            {taskId && onWillDoTodayChange && (
            <div
              className="will-do-switcher-wrapper"
              onClick={e => e.stopPropagation()}
            >
              <div
                className="will-do-switcher-container"
                onMouseMove={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const width = rect.width;
                  const section = Math.floor((x / width) * 3);

                  let tooltip = '';
                  if (section === 0) tooltip = 'Pinned';
                  else if (section === 1) tooltip = 'Today';
                  else tooltip = 'None';

                  setSwitcherTooltip(tooltip);
                  setSwitcherTooltipPosition({
                    x: e.clientX,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => {
                  setSwitcherTooltip(null);
                  setSwitcherTooltipPosition(null);
                }}
              >
                <SwitchSelector
                  name={`modal-will-do-switcher-${taskId}`}
                  onChange={(value: unknown) => {
                    const strValue = value as string;
                    const mappedValue =
                      strValue === 'none'
                        ? false
                        : (strValue as 'pinned' | 'today');
                    // Update local state immediately for instant visual feedback
                    setLocalWillDoToday(mappedValue);
                    // Update parent state
                    onWillDoTodayChange(taskId, mappedValue);
                  }}
                  options={[
                    {
                      label: (
                        <TbPin
                          size={18}
                          color={localWillDoToday === 'pinned' ? '#1976d2' : '#495057'}
                        />
                      ),
                      value: 'pinned',
                      selectedBackgroundColor: '#e3f2fd',
                    },
                    {
                      label: (
                        <TbClockDown
                          size={18}
                          color={localWillDoToday === 'today' ? '#1976d2' : '#495057'}
                        />
                      ),
                      value: 'today',
                      selectedBackgroundColor: '#e3f2fd',
                    },
                    {
                      label: (
                        <TbX
                          size={18}
                          color={localWillDoToday === false ? '#1976d2' : '#495057'}
                        />
                      ),
                      value: 'none',
                      selectedBackgroundColor: '#e3f2fd',
                    },
                  ]}
                  initialSelectedIndex={
                    willDoToday === 'pinned' ? 0 : willDoToday === 'today' ? 1 : 2
                  }
                  backgroundColor={'#f1f3f5'}
                  fontColor={'#495057'}
                  selectedFontColor={'#1976d2'}
                  fontSize={16}
                  optionBorderRadius={20}
                  wrapperBorderRadius={20}
                />
              </div>
              {switcherTooltip && switcherTooltipPosition && (
                <div
                  className="switcher-tooltip-floating"
                  style={{
                    left: switcherTooltipPosition.x,
                    top: switcherTooltipPosition.y,
                  }}
                >
                  {switcherTooltip}
                </div>
              )}
            </div>
          )}
          {taskId && onRecurrenceChange && (
            <div
              className="modal-recurrence-container"
              ref={recurrenceDropdownRef}
              onClick={e => e.stopPropagation()}
            >
              <button
                className={`recurrence-btn ${recurrenceDays ? 'active' : ''}`}
                onClick={() => setIsRecurrenceDropdownOpen(!isRecurrenceDropdownOpen)}
                title={recurrenceDays ? `Repeats every ${recurrenceDays} day${recurrenceDays > 1 ? 's' : ''}` : 'Set recurrence'}
              >
                <TbRepeat size={18} />
                {recurrenceDays && <span className="recurrence-badge">{recurrenceDays}d</span>}
              </button>
              {isRecurrenceDropdownOpen && (
                <div className="recurrence-dropdown">
                  <div className="recurrence-presets">
                    <button
                      className={`recurrence-preset ${recurrenceDays === 1 ? 'active' : ''}`}
                      onClick={() => handleSetRecurrence(1)}
                    >
                      Daily
                    </button>
                    <button
                      className={`recurrence-preset ${recurrenceDays === 7 ? 'active' : ''}`}
                      onClick={() => handleSetRecurrence(7)}
                    >
                      Weekly
                    </button>
                    <button
                      className={`recurrence-preset ${recurrenceDays === 14 ? 'active' : ''}`}
                      onClick={() => handleSetRecurrence(14)}
                    >
                      Biweekly
                    </button>
                  </div>
                  <div className="recurrence-custom">
                    <input
                      type="number"
                      min="1"
                      placeholder="Custom days"
                      value={customRecurrenceDays}
                      onChange={e => setCustomRecurrenceDays(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleCustomRecurrenceSubmit();
                        }
                      }}
                    />
                    <button
                      onClick={handleCustomRecurrenceSubmit}
                      disabled={!customRecurrenceDays || parseInt(customRecurrenceDays, 10) <= 0}
                    >
                      Set
                    </button>
                  </div>
                  {recurrenceDays && (
                    <button
                      className="recurrence-remove"
                      onClick={handleRemoveRecurrence}
                    >
                      Remove recurrence
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
          <div className="modal-actions-row-2">
          {taskId && onSnooze && (
            <div
              className="modal-snooze-container"
              ref={snoozeDropdownRef}
              onClick={e => e.stopPropagation()}
            >
              <button
                className="snooze-btn"
                onClick={() => setIsSnoozeDropdownOpen(!isSnoozeDropdownOpen)}
              >
                ⏰
              </button>
              {isSnoozeDropdownOpen && (
                <div className="snooze-dropdown modal-snooze-dropdown">
                  <button
                    className="snooze-option"
                    onClick={handleTomorrowSnooze}
                  >
                    Tomorrow
                  </button>
                  <button
                    className="snooze-option"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  >
                    Select date
                  </button>
                  {showDatePicker && (
                    <div className="date-picker-container">
                      <DatePicker
                        value={selectedDate}
                        onChange={handleDateSelect}
                        minDate={(() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return tomorrow;
                        })()}
                        className="date-picker"
                        firstDayOfWeek={0}
                      />
                      <button
                        className="date-submit-btn"
                        onClick={handleDateSubmit}
                        disabled={!selectedDate}
                      >
                        Confirm
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {taskId && onUrgencyChange && (
            <div
              className="modal-urgency-container task-urgency-container"
              ref={urgencyDropdownRef}
              onClick={e => e.stopPropagation()}
            >
              <span
                className="task-urgency"
                onClick={() => setIsUrgencyDropdownOpen(!isUrgencyDropdownOpen)}
              >
                <span
                  style={{
                    fontWeight: 600,
                    color:
                      (urgencyNumber ?? 1) >= 7
                        ? 'rgb(250, 82, 82)'
                        : (urgencyNumber ?? 1) >= 4
                          ? 'rgb(253, 126, 20)'
                          : 'rgb(134, 142, 150)',
                  }}
                >
                  {urgencyNumber?.toFixed(1)}
                </span>
              </span>
              {isUrgencyDropdownOpen && (
                <div className="urgency-select-dropdown">
                  <Slider
                    min={0}
                    max={10}
                    step={1}
                    value={pendingUrgencyNumber}
                    onChange={handlePendingUrgencyChange}
                    size="xl"
                    style={{
                      width: '100%',
                      marginTop: 4,
                      marginRight: 5,
                    }}
                    label={`${
                      pendingUrgencyNumber >= 8
                        ? 'High Urgency ❗️'
                        : pendingUrgencyNumber >= 4
                          ? 'Medium Urgency ⚠️'
                          : pendingUrgencyNumber === 0
                            ? 'No Urgency'
                            : 'Low Urgency ℹ️'
                    }`}
                    color={
                      pendingUrgencyNumber >= 8
                        ? 'red'
                        : pendingUrgencyNumber >= 4
                          ? 'orange'
                          : 'gray'
                    }
                    thumbChildren={
                      <span style={{ fontWeight: 600 }}>
                        {pendingUrgencyNumber}
                      </span>
                    }
                    styles={{
                      thumb: {
                        borderWidth: 2,
                        borderColor:
                          pendingUrgencyNumber >= 8
                            ? 'red'
                            : pendingUrgencyNumber >= 4
                              ? 'orange'
                              : 'gray',
                        padding: 11,
                      },
                      label: {
                        fontSize: '14px',
                        fontWeight: 500,
                        backgroundColor:
                          pendingUrgencyNumber >= 8
                            ? 'rgb(250, 82, 82)'
                            : pendingUrgencyNumber >= 4
                              ? 'rgb(253, 126, 20)'
                              : 'rgb(134, 142, 150)',
                      },
                    }}
                  />
                  {hasUrgencyChanges && (
                    <div
                      className="urgency-actions"
                      style={{ marginTop: '12px', display: 'flex', gap: '8px' }}
                    >
                      <button
                        className="urgency-accept-btn"
                        onClick={handleAcceptUrgency}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        Accept
                      </button>
                      <button
                        className="urgency-cancel-btn"
                        onClick={handleCancelUrgency}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {taskId && onTimerToggle && (
            <button
              className="modal-timer-btn"
              onClick={e => {
                e.stopPropagation();
                handleTimerToggle();
              }}
              title={timerStartedAt ? 'Stop timer' : 'Start timer'}
            >
              {timerStartedAt ? '⏹️' : '▶️'}
            </button>
          )}
          </div>
        </div>
      </div>
      {taskId && (
        <ShareTaskModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          taskId={taskId}
          onShareSuccess={(email, userId, photoURL) => {
            if (onShareSuccess) {
              onShareSuccess(email, userId, photoURL);
            }
          }}
        />
      )}
    </div>
  );
};
